// @flow
import React, { type Node, Component } from 'react';
import styled from 'styled-components';
import onClickOutside from 'react-onclickoutside';

import YearMonthRangePicker from './YearMonthRangePicker';
import Popover from './Popover';
import Button from './Button';

const Wrapper = styled.div`
  position: relative;
`;

const PopoverWrap = styled.div`
  position: absolute;
  top: 100%;
  z-index: 1;
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px 16px 6px;
`;

const ApplyButton = styled(Button)`
  width: 120px;
  margin-top: 8px;
`;

export type YearMonthRangePopoverProps = {
  defaultDisplayedYear: number,
  children: ((boolean) => void) => Node,
  defaultStartYearMonth?: string,
  defaultEndYearMonth?: string,
  selectionType?: 'single' | 'range',
  calendarType?: 'single' | 'multiple',
  onRangeSelect: (startYearMonth: string | null, endYearMonth: string | null) => void,
};

export type YearMonthRangePopoverState = {
  startYearMonth: string | null,
  endYearMonth: string | null,
  popOverActive: boolean,
};

class YearMonthRangePopover extends Component<
  YearMonthRangePopoverProps,
  YearMonthRangePopoverState,
> {
  static defaultProps = {
    defaultStartYearMonth: undefined,
    defaultEndYearMonth: undefined,
    selectionType: 'range',
    calendarType: 'single',
  };

  constructor(props) {
    super(props);

    this.state = {
      popOverActive: false,
      startYearMonth: null,
      endYearMonth: null,
    };
  }

  togglePopover = show => {
    this.setState(prevState => ({
      popOverActive: typeof show === 'boolean' ? show : !prevState.popOverActive,
    }));
  };

  handleYearMonthRangeSelect = (startYearMonth, endYearMonth) => {
    this.setState({
      startYearMonth,
      endYearMonth,
    });
  };

  handleApplyDate = () => {
    const { startYearMonth, endYearMonth } = this.state;

    this.props.onRangeSelect(startYearMonth, endYearMonth);

    this.setState({
      startYearMonth: null,
      endYearMonth: null,
      popOverActive: false,
    });
  };

  // Expected by `onClickOutside` which will handle clicks on the document to dismiss the popover.
  handleClickOutside = () => {
    this.setState({ popOverActive: false });
  };

  render() {
    const {
      children,
      selectionType,
      calendarType,
      defaultDisplayedYear,
      defaultEndYearMonth,
      defaultStartYearMonth,
    } = this.props;
    const { popOverActive, startYearMonth, endYearMonth } = this.state;

    const applyButtonEnabled = startYearMonth && endYearMonth;
    const displayedYear = defaultDisplayedYear || new Date().getFullYear();

    return (
      <Wrapper>
        {children(this.togglePopover)}
        {popOverActive && (
          <PopoverWrap>
            <Popover data-test-id="year-month-range-popover">
              <PopoverContent>
                <YearMonthRangePicker
                  defaultDisplayedYear={displayedYear}
                  defaultStartYearMonth={defaultStartYearMonth}
                  defaultEndYearMonth={defaultEndYearMonth}
                  selectionType={selectionType}
                  calendarType={calendarType}
                  onRangeSelect={this.handleYearMonthRangeSelect}
                />
                <ApplyButton
                  data-test-id="year-month-range-apply-date"
                  disabled={!applyButtonEnabled}
                  onClick={this.handleApplyDate}
                  styleType="primary"
                  type="button"
                >
                  Apply
                </ApplyButton>
              </PopoverContent>
            </Popover>
          </PopoverWrap>
        )}
      </Wrapper>
    );
  }
}

export default onClickOutside(YearMonthRangePopover);
